import React, { useEffect, useRef, useState } from 'react';

import { useTokenPairStore } from '@/features/tokenPairs';
import { isLatencyDebugHost } from '@/lib/latencyDebug';
import { cn } from '@/lib/utils';

import type { PriceConfig } from '../hooks/usePriceConfig';
import {
    selectActiveTradeFillMs,
    selectActiveTradeRestored,
    selectActiveTradeVisual,
    useTradingStore,
} from '../store';
import type { ClosedTrade } from '../types';

/**
 * Result visuals, wired to real trade lifecycle events:
 *
 *  · "POSITION OPENED" toast anchored to the chart when a position opens —
 *    the single fill notification (useTradeExecution no longer raises a
 *    sonner "Long filled" toast); on latency-debug hosts it also carries the
 *    measured request→fill milliseconds.
 *  · "POSITION CLOSED" toast with PnL badge when one closes (any result)
 *
 * Sharing is deliberately NOT part of this: a card that auto-opened on wins
 * broke the trading flow. It lives behind the share buttons on the position
 * and history rows instead — see PnlShareDialog.
 */

interface SpeedResultFxProps {
    priceConfig: PriceConfig;
}

const OPEN_TOAST_MS = 4000;
const CLOSE_TOAST_MS = 6000;

const SpeedResultFx: React.FC<SpeedResultFxProps> = ({ priceConfig }) => {
    const activePosition = useTradingStore(selectActiveTradeVisual);
    // A position restored from the backend (hard refresh, REST reconciliation,
    // snapshot resume) also transitions null → position, but it is NOT a fill —
    // announcing it would re-fire "position opened" on every F5. The execution
    // state records how the position entered the client (`live` vs `restored`),
    // which is exact; deriving it from the age of `openedAt` would be a guess
    // that misfires on a reload seconds after an open.
    const isRestoredPosition = useTradingStore(selectActiveTradeRestored);
    const fillMs = useTradingStore(selectActiveTradeFillMs);
    const overlayTrade = useTradingStore((s) => s.overlayTrade);
    const selectedCoin = useTokenPairStore((s) => s.selectedCoin);

    // ── "Position opened" toast on null → position transition ────────────
    const [openToast, setOpenToast] = useState<{
        side: 'LONG' | 'SHORT';
        entry: number;
        at: string;
        /** Measured request→fill ms; dev-host instrument, null in production. */
        fillMs: number | null;
    } | null>(null);
    const hadPositionRef = useRef(false);
    useEffect(() => {
        if (activePosition && !hadPositionRef.current && !isRestoredPosition) {
            setOpenToast({
                side: activePosition.type,
                entry: activePosition.entryPrice,
                at: new Date(activePosition.timestamp).toLocaleTimeString(
                    'en-GB',
                    { hour12: false },
                ),
                fillMs: isLatencyDebugHost() ? fillMs : null,
            });
            hadPositionRef.current = true;
            return;
        }
        hadPositionRef.current = activePosition !== null;
    }, [activePosition, isRestoredPosition, fillMs]);

    // ── Close toast, captured from overlayTrade ──────────────────────────
    const [closeToast, setCloseToast] = useState<ClosedTrade | null>(null);
    const lastClosedRef = useRef<number | null>(null);
    useEffect(() => {
        if (!overlayTrade || overlayTrade.timestamp === lastClosedRef.current)
            return;
        lastClosedRef.current = overlayTrade.timestamp;
        setOpenToast(null);
        // closeMs mirrors fillMs above: a dev instrument, stripped outside the
        // latency-debug hosts so the card carries no internal metric in prod.
        setCloseToast(
            isLatencyDebugHost()
                ? overlayTrade
                : { ...overlayTrade, closeMs: undefined },
        );
    }, [overlayTrade]);

    // ── Auto-dismiss ─────────────────────────────────────────────────────
    // Each card's timer hangs off the card itself, NOT off the state that
    // raised it. It used to be started inside the effects above and torn down
    // by their cleanup, so any later change to the position — the status
    // walking pending → open, reconciliation refining the entry price, the
    // fill latency landing — ran the cleanup, cancelled the dismissal, and
    // then skipped rescheduling it because the had-position guard was already
    // set. The card stayed on screen forever. Keyed on the card object (set
    // exactly once per fill/close), the timer survives those updates.
    useEffect(() => {
        if (!openToast) return;
        const timer = setTimeout(() => setOpenToast(null), OPEN_TOAST_MS);
        return () => clearTimeout(timer);
    }, [openToast]);

    useEffect(() => {
        if (!closeToast) return;
        const timer = setTimeout(() => setCloseToast(null), CLOSE_TOAST_MS);
        return () => clearTimeout(timer);
    }, [closeToast]);

    return (
        <>
            {openToast && !closeToast && (
                <div className="st-toast st-toast--open" role="status">
                    <span className="st-toast__title">Position opened</span>
                    <span className="st-toast__row">
                        <span>Side</span>
                        <b
                            className={
                                openToast.side === 'LONG'
                                    ? 'text-primary'
                                    : 'text-destructive'
                            }
                        >
                            {openToast.side}
                        </b>
                    </span>
                    <span className="st-toast__row">
                        <span>Open price</span>
                        <b>${priceConfig.formatDisplay(openToast.entry)}</b>
                    </span>
                    <span className="st-toast__row">
                        <span>Time executed</span>
                        <b>
                            {openToast.at}
                            {openToast.fillMs !== null &&
                                ` · ${openToast.fillMs}ms`}
                        </b>
                    </span>
                </div>
            )}

            {closeToast && (
                <div
                    className={cn(
                        'st-toast',
                        closeToast.pnlValue >= 0
                            ? 'st-toast--win'
                            : 'st-toast--loss',
                    )}
                    role="status"
                >
                    <span className="st-toast__title">Position closed</span>
                    <span className="st-toast__row">
                        <span>{selectedCoin} close price</span>
                        <b>
                            ${priceConfig.formatDisplay(closeToast.exitPrice)}
                            {closeToast.closeMs !== undefined &&
                                ` · ${closeToast.closeMs}ms`}
                        </b>
                    </span>
                    <span className="st-toast__pnlrow">
                        <span className="st-toast__pnl">
                            {closeToast.pnlValue >= 0 ? '+' : ''}$
                            {closeToast.pnlValue.toFixed(2)}
                        </span>
                        <span className="st-toast__badge">
                            {closeToast.pnlValue >= 0 ? '+' : ''}
                            {closeToast.pnlPercent.toFixed(2)}%
                        </span>
                        <span className="st-toast__slashes">//</span>
                    </span>
                </div>
            )}
        </>
    );
};

export default SpeedResultFx;
